import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, Brain, TrendingUp, Shield } from 'lucide-react';

const LoginPage: React.FC = () => {
    const { loginWithGoogle } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGoogleSignIn = async () => {
        try {
            setIsLoading(true);
            setError(null);
            await loginWithGoogle();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sign in');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
            <div className="max-w-6xl w-full grid md:grid-cols-2 gap-8 items-center">
                {/* Left side - Branding */}
                <div className="text-white space-y-6">
                    <div className="flex items-center gap-3">
                        <Brain className="w-12 h-12 text-blue-300" />
                        <h1 className="text-5xl font-bold">Magnus Resolve</h1>
                    </div>
                    <p className="text-xl text-blue-100">
                        AI-Powered Failure Analysis & Resolution Platform
                    </p>

                    <div className="space-y-4 mt-8">
                        <div className="flex items-start gap-3">
                            <TrendingUp className="w-6 h-6 text-blue-300 mt-1" />
                            <div>
                                <h3 className="font-semibold">Advanced AI Detection</h3>
                                <p className="text-sm text-blue-200">
                                    Leverage cutting-edge AI to detect, analyze, and resolve failures faster
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Shield className="w-6 h-6 text-blue-300 mt-1" />
                            <div>
                                <h3 className="font-semibold">Root Cause Analysis</h3>
                                <p className="text-sm text-blue-200">
                                    Deep learning models identify true root causes, not just symptoms
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Brain className="w-6 h-6 text-blue-300 mt-1" />
                            <div>
                                <h3 className="font-semibold">Knowledge Base Integration</h3>
                                <p className="text-sm text-blue-200">
                                    Access historical failure data and industry best practices
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right side - Login Form */}
                <div className="bg-white rounded-2xl shadow-2xl p-8">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
                        <p className="text-gray-600">Sign in to access Magnus Resolve</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    <button
                        onClick={handleGoogleSignIn}
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-blue-500 hover:shadow-lg text-gray-700 font-semibold py-4 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                <span>Signing in...</span>
                            </>
                        ) : (
                            <>
                                <LogIn className="w-5 h-5 group-hover:text-blue-600 transition-colors" />
                                <span>Continue with Google</span>
                            </>
                        )}
                    </button>

                    <div className="mt-6 text-center text-sm text-gray-500">
                        <p>Secured by Magnus Core RBAC</p>
                        <p className="mt-2">Shared authentication with DFMEA & PFMEA</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
